import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  Divider,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Header from '../../components/Header/Header';
import './BookingConfirmation.css';

const PAY_STORAGE_PREFIX = 'tourasco_pay_';

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeKey = searchParams.get('payment_resume');
  const sessionId = searchParams.get('session_id');

  const [copiedRef, setCopiedRef] = useState('');

  const {
    bookingRef,
    yourRef,
    hotel,
    guestDetails,
    totalPrice,
    checkInDate,
    nights,
    adults,
    children
  } = location.state || {};

  const [stripeError, setStripeError] = useState('');
  const [stripeWorking, setStripeWorking] = useState(Boolean(resumeKey && sessionId));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (resumeKey && sessionId) {
      let cancelled = false;
      const storageKey = PAY_STORAGE_PREFIX + resumeKey;

      (async () => {
        try {
          const raw = sessionStorage.getItem(storageKey);
          if (!raw) {
            throw new Error(
              'Booking session expired. Please start again from the hotel page.',
            );
          }

          let payload;
          try {
            payload = JSON.parse(raw);
          } catch {
            throw new Error('Invalid booking session data.');
          }

          // Flight checkout must finish on /flights/booking-confirmation (Duffel). If Stripe still
          // redirects here (old Edge deploy missing successPath), hand off without calling G2.
          if (payload.type === 'flight') {
            navigate(`/flights/booking-confirmation?${searchParams.toString()}`, {
              replace: true,
            });
            return;
          }

          const { verifyStripeCheckoutSession } = await import('../../lib/stripeFunctions');
          const verified = await verifyStripeCheckoutSession(sessionId);
          if (!verified.ok) {
            throw new Error(verified.error || 'Payment was not completed');
          }

          sessionStorage.removeItem(storageKey);

          const { default: g2TravelApi } = await import('../../services/g2TravelApi');
          const result = await g2TravelApi.createBooking(payload.bookingParams);
          if (result.error) {
            throw new Error(
              result.error.description || result.error.code || 'Booking failed',
            );
          }

          if (cancelled) return;

          navigate('/booking-confirmation', {
            replace: true,
            state: {
              bookingRef:
                result.bookingRef || result.booking?.bookingRef || `REF-${Date.now()}`,
              yourRef: payload.bookingParams.clientRef,
              hotel: payload.hotel,
              guestDetails: payload.guestDetails,
              totalPrice: payload.totalPrice,
              checkInDate: payload.checkInDate,
              checkOutDate: payload.checkOutDate,
              nights: payload.nights,
              adults: payload.adults,
              children: payload.children,
            },
          });
        } catch (e) {
          if (!cancelled) {
            setStripeError(e.message || 'Could not complete booking after payment.');
          }
        } finally {
          if (!cancelled) {
            setStripeWorking(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (!bookingRef || !hotel) {
      navigate('/');
    }
    return undefined;
  }, [resumeKey, sessionId, bookingRef, hotel, navigate, searchParams]);

  const copyToClipboard = (text, refType) => {
    navigator.clipboard.writeText(text);
    setCopiedRef(refType);
    setTimeout(() => setCopiedRef(''), 2000);
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  if (stripeWorking) {
    return (
      <>
        <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
            p: 2,
          }}
        >
          <Typography variant="h6">Confirming your payment and booking…</Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait — do not close this page.
          </Typography>
        </Box>
      </>
    );
  }

  if (stripeError) {
    return (
      <>
        <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
        <Container sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {stripeError}
          </Alert>
          <Button variant="contained" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Container>
      </>
    );
  }

  if (!bookingRef || !hotel) {
    return null;
  }

  return (
    <>
      <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
      <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', pb: 6, pt: 4 }}>
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: 5, borderRadius: 3 }}>
            {/* Success Icon */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box
                className="success-icon-container"
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  backgroundColor: '#4CAF50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
                  animation: 'scaleIn 0.5s ease-out'
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 80, color: 'white' }} />
              </Box>
            </Box>

            {/* Success Message */}
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
                fontWeight: 700,
                mb: 2,
                color: '#112A41',
                fontSize: { xs: '28px', sm: '36px' }
              }}
            >
              Booking Confirmed!
            </Typography>

            <Typography variant="body1" sx={{ textAlign: 'center', mb: 1, color: '#666' }}>
              Your booking is confirmed
            </Typography>
            {guestDetails?.email && (
              <Typography variant="body2" sx={{ textAlign: 'center', mb: 4, color: '#666' }}>
                Confirmation email sent to: <strong>{guestDetails.email}</strong>
              </Typography>
            )}

            {/* Booking Reference Card */}
            <Card
              sx={{
                mb: 4,
                backgroundColor: '#F0F8FF',
                border: '2px solid #0071C2',
                borderRadius: 2
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#112A41' }}>
                  Booking Reference
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Booking Reference:
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(bookingRef, 'booking')}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: '#112A41',
                      fontSize: '18px'
                    }}
                  >
                    {bookingRef}
                  </Typography>
                  {copiedRef === 'booking' && (
                    <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                      Copied!
                    </Typography>
                  )}
                </Box>

                {yourRef && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Your Reference:
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(yourRef, 'your')}
                        sx={{ ml: 1 }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: '#666'
                      }}
                    >
                      {yourRef}
                    </Typography>
                    {copiedRef === 'your' && (
                      <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                        Copied!
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Booking Details Card */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                  {/* Left Side - Hotel Info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <HotelIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {hotel?.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                      <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {hotel?.address1}
                        </Typography>
                        {hotel?.cityName && (
                          <Typography variant="body2" color="text.secondary">
                            {hotel?.cityName}
                            {hotel?.countryName && `, ${hotel?.countryName}`}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                  <Divider sx={{ display: { xs: 'block', md: 'none' }, my: 2 }} />

                  {/* Right Side - Booking Info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CalendarTodayIcon color="primary" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Check-in
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                      {new Date(checkInDate).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Duration
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {nights} {nights === 1 ? 'night' : 'nights'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PeopleIcon color="primary" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Guests
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {adults} {adults === 1 ? 'adult' : 'adults'}
                      {children > 0 && `, ${children} ${children === 1 ? 'child' : 'children'}`}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Total Price
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: '#112A41',
                        fontSize: { xs: '28px', sm: '36px' }
                      }}
                    >
                      £{totalPrice?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Confirmation Alert */}
            <Alert
              icon={<EmailIcon />}
              severity="success"
              sx={{
                mb: 4,
                backgroundColor: '#E8F5E9',
                border: '1px solid #4CAF50',
                '& .MuiAlert-icon': {
                  color: '#4CAF50'
                }
              }}
            >
              <Typography variant="body2">
                <strong>✓ A confirmation email has been sent.</strong> Please save your booking reference number.
              </Typography>
            </Alert>

            {/* What's Next Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#112A41' }}>
                What's next?
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  Check your email for booking confirmation
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  Save your booking reference number
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  Arrive at hotel on your check-in date
                </Typography>
                <Typography component="li" variant="body1">
                  Present your booking reference at reception
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={() => {
                  navigate('/hotels');
                  window.scrollTo(0, 0);
                }}
                sx={{
                  py: 1.5,
                  borderColor: '#112A41',
                  color: '#112A41',
                  '&:hover': {
                    borderColor: '#0d1f33',
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Browse More Hotels
              </Button>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => {
                  navigate('/profile');
                  window.scrollTo(0, 0);
                }}
                sx={{
                  py: 1.5,
                  backgroundColor: '#112A41',
                  '&:hover': {
                    backgroundColor: '#0d1f33'
                  }
                }}
              >
                View My Bookings
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default BookingConfirmation;
